import React from 'react';
import { styled, css, themeGet, Box, Dropdown, Icon, Flex, Button, Container, Text } from '@goodrx/matisse';
import { useSynapse } from 'app/lib/synaptik';

import { mediaMaxWidth } from 'app/lib/utils';
import AppLogo from 'app/components/AppLogo/AppLogo';
import { UniversalLink } from 'app/components/links/UniversalLink';
import MessageIcon from 'app/components/core/message-icon';
import { withRouter } from 'react-router-dom';

export const Header = styled.header`
  display: flex;
  align-items: center;
  height: 60px;
  flex-shrink: 0;

  ${(p) => css`
    background: ${themeGet('components.header.bgColor', 'white')};
    border-bottom: 1px solid ${themeGet('components.header.bgColor', p.theme.colors.greyLight)};
    color: ${p.theme.colors.greyDarkest};
    margin-bottom: 32px;

    ${mediaMaxWidth({
      sm: css`
        margin-bottom: 24px;
        z-index: 999;
        width: 100%;
        position: sticky;
        top: 0;
      `,
    })}
  `};
`;

const HeaderText = styled(Flex)`
  ${(p) => css`
    align-items: center;
    color: ${themeGet('components.header.fontColor', p.theme.colors.greyDarkest)};
    cursor: pointer;
    margin-left: 24px;

    span {
      font-weight: 500;
      margin-left: 8px;
    }

    .mdi {
      position: relative;
      top: 1px;
      font-size: 20px;

      ${mediaMaxWidth({
        sm: css`
          font-size: 24px;
        `,
      })}
    }
  `}
`;

const HideOnMobile = styled.div`
  ${mediaMaxWidth({
    sm: `display: none;`,
  })}
`;

const HideOnTinyScreens = styled.div<any>(() => ``);

const StyledAvatar = styled.div<any>`
  ${(p: any) => css`
    height: ${p.size}px;
    width: ${p.size}px;
    border-radius: ${p.size}px;
    background: ${p.theme.colors.primary};
    color: white;
    text-align: center;
    line-height: ${p.size}px;
    font-size: 10px;
    margin-right: 8px;
  `};
`;

const Avatar = ({ initials = '' }) => <StyledAvatar size={25}>{initials}</StyledAvatar>;

export const AppHeader = withRouter(({ history }) => {
  const [user, authenticated, logout, toggleChat] = useSynapse(({ user: userStore, chat }) => [
    userStore.state.profile,
    userStore.state.authenticated,
    userStore.logout,
    chat.toggleChat,
  ]);

  const handleLogout = async () => {
    await logout();
    history.replace('/login');
  };

  return (
    <Header>
      <Container>
        <Flex alignItems="center">
          <UniversalLink to="/">
            <AppLogo />
          </UniversalLink>

          <Box flex={1} />

          <Flex as="ul">
            {!authenticated && (
              <>
                <Box display={{ _: 'none', sm: 'initial' }}>
                  <UniversalLink to="/login">
                    <Button variant="secondary" size="sm">
                      Sign In
                    </Button>
                  </UniversalLink>
                </Box>

                <HideOnTinyScreens>
                  <UniversalLink to="/register">
                    <Button ml={[0, 3]} size="sm">
                      Get Started
                    </Button>
                  </UniversalLink>
                </HideOnTinyScreens>
              </>
            )}

            {authenticated && (
              <Flex alignItems="center">
                <HeaderText onClick={toggleChat}>
                  <Flex alignItems="center">
                    <MessageIcon />
                    <HideOnMobile>
                      <Text>Messages</Text>
                    </HideOnMobile>
                  </Flex>
                </HeaderText>

                <Dropdown
                  placement="bottom-end"
                  positionFixed
                  boundariesElement="scrollParent"
                  width={250}
                  trigger={
                    <HeaderText>
                      <Icon name="account-circle" />
                      <HideOnMobile>
                        <Text>Account</Text>
                      </HideOnMobile>
                      <Icon name="chevron-down" style={{ top: 2, marginLeft: 2 }} />
                    </HeaderText>
                  }>
                  <>
                    {user?.firstName && user.lastName && (
                      <UniversalLink to="/account" style={{ color: 'inherit' }}>
                        <Dropdown.Item>
                          <Flex alignItems="center">
                            <Avatar initials={`${user.firstName[0]}${user.lastName[0]}`} />

                            <Box>
                              <strong style={{ display: 'block' }}>
                                {user.firstName} {user.lastName}
                              </strong>
                              <Text color="greyDarker">{user.email}</Text>
                            </Box>
                          </Flex>
                        </Dropdown.Item>
                      </UniversalLink>
                    )}

                    <Dropdown.Divider />
                    <Dropdown.Title>Your Care</Dropdown.Title>

                    <UniversalLink to="/account/start" style={{ color: 'inherit' }}>
                      <Dropdown.Item icon="hospital-box">Services</Dropdown.Item>
                    </UniversalLink>

                    <UniversalLink to="/account/visits" style={{ color: 'inherit' }}>
                      <Dropdown.Item icon="history">Visit History</Dropdown.Item>
                    </UniversalLink>

                    <UniversalLink to="/account/prescriptions" style={{ color: 'inherit' }}>
                      <Dropdown.Item icon="pill">Prescriptions</Dropdown.Item>
                    </UniversalLink>

                    <Dropdown.Title>Your Account</Dropdown.Title>

                    <UniversalLink to="/account/edit" style={{ color: 'inherit' }}>
                      <Dropdown.Item icon="pencil">Edit Profile</Dropdown.Item>
                    </UniversalLink>

                    <UniversalLink to="/account/notifications" style={{ color: 'inherit' }}>
                      <Dropdown.Item icon="bell">Notifications</Dropdown.Item>
                    </UniversalLink>

                    <UniversalLink to="/account/payments" style={{ color: 'inherit' }}>
                      <Dropdown.Item icon="credit-card-outline">Payment Methods</Dropdown.Item>
                    </UniversalLink>

                    <Dropdown.Divider />

                    <Dropdown.Item onClick={handleLogout} style={{ cursor: 'pointer' }} color="red">
                      Sign Out
                    </Dropdown.Item>
                  </>
                </Dropdown>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Container>
    </Header>
  );
});
